import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiEdit2,
  FiPlus,
  FiSearch,
  FiTrash2,
} from "react-icons/fi";
import SizeModal from "../../components/masters/SizeModal";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import ClientPagination, { useClientPagination } from "../../components/common/ClientPagination";

export default function SizesMasterPage() {
  const { accessToken } = useAuth();

  const [sizes, setSizes] = useState([]);
  const [sizeGroups, setSizeGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingSize, setEditingSize] = useState(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const [sizesResponse, sizeGroupsResponse] = await Promise.all([
        apiRequest("/sizes", { accessToken }),
        apiRequest("/size-groups", { accessToken }),
      ]);

      setSizes(
        Array.isArray(sizesResponse)
          ? sizesResponse
          : sizesResponse?.data || [],
      );

      setSizeGroups(
        Array.isArray(sizeGroupsResponse)
          ? sizeGroupsResponse
          : sizeGroupsResponse?.data || [],
      );
    } catch (requestError) {
      setError(requestError.message || "Unable to load sizes.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredSizes = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return sizes;

    return sizes.filter((size) =>
      [
        size.name,
        size.code,
        size.sizeGroup?.name,
        size.sizeGroup?.code,
      ].some((value) => value?.toLowerCase().includes(query)),
    );
  }, [search, sizes]);

  const stats = useMemo(() => {
    const activeSizes = sizes.filter((size) => size.isActive).length;
    const linkedGroupIds = new Set(
      sizes
        .map((size) => size.sizeGroupId)
        .filter(Boolean),
    );

    return {
      total: sizes.length,
      active: activeSizes,
      inactive: sizes.length - activeSizes,
      groupsUsed: linkedGroupIds.size,
    };
  }, [sizes]);
  const sizesPagination = useClientPagination(filteredSizes);

  const handleSave = async (form) => {
    setError("");

    try {
      if (editingSize) {
        await apiRequest(`/sizes/${editingSize.id}`, {
          method: "PATCH",
          accessToken,
          body: form,
        });
      } else {
        // CreateSizeDto does not need isActive; new sizes start active.
        await apiRequest("/sizes", {
          method: "POST",
          accessToken,
          body: {
            code: form.code,
            name: form.name,
            sizeGroupId: form.sizeGroupId,
            sortOrder: form.sortOrder,
          },
        });
      }

      setShowModal(false);
      setEditingSize(null);
      await loadData();
    } catch (requestError) {
      setError(requestError.message || "Unable to save size.");
    }
  };

  const handleDelete = async (size) => {
    const confirmed = window.confirm(
      `Delete "${size.name}"? It cannot be deleted if product variants still use it.`,
    );

    if (!confirmed) return;

    setError("");

    try {
      await apiRequest(`/sizes/${size.id}`, {
        method: "DELETE",
        accessToken,
      });

      await loadData();
    } catch (requestError) {
      setError(requestError.message || "Unable to delete size.");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSize(null);
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Masters / Sizes</p>

          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Sizes Management
          </h1>

          <p className="mt-2 text-gray-500">
            Manage reusable product sizes and their size groups.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingSize(null);
            setShowModal(true);
          }}
          className="flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-800 px-5 text-white hover:bg-primary-900"
        >
          <FiPlus />
          Add Size
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-8 grid gap-5 md:grid-cols-4">
        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Total Sizes</p>
          <h2 className="mt-2 text-3xl font-bold">{stats.total}</h2>
        </div>

        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Active</p>
          <h2 className="mt-2 text-3xl font-bold text-green-600">
            {stats.active}
          </h2>
        </div>

        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Inactive</p>
          <h2 className="mt-2 text-3xl font-bold text-red-500">
            {stats.inactive}
          </h2>
        </div>

        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Size Groups Used</p>
          <h2 className="mt-2 text-3xl font-bold text-primary-800">
            {stats.groupsUsed}
          </h2>
        </div>
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="border-b border-gray-100 p-5">
          <div className="relative max-w-md">
            <FiSearch
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              type="text"
              placeholder="Search sizes or size groups..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-12 w-full rounded-xl border border-gray-200 pl-12 pr-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Size
                </th>

                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Code
                </th>

                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Size Group
                </th>

                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Order
                </th>

                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Status
                </th>

                <th className="px-6 py-4 text-right text-xs uppercase text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-sm text-gray-500"
                  >
                    Loading sizes...
                  </td>
                </tr>
              ) : filteredSizes.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-sm text-gray-500"
                  >
                    No sizes found.
                  </td>
                </tr>
              ) : (
                sizesPagination.pageItems.map((size) => (
                  <tr
                    key={size.id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      {size.name}
                    </td>

                    <td className="px-6 py-4">
                      <span className="rounded-lg bg-gray-100 px-2.5 py-1 font-mono text-sm text-gray-700">
                        {size.code}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700">
                      {size.sizeGroup ? (
                        <div>
                          <p className="font-medium">
                            {size.sizeGroup.name}
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            {size.sizeGroup.code}
                          </p>
                        </div>
                      ) : (
                        <span className="text-gray-400">Not assigned</span>
                      )}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-600">
                      {size.sortOrder}
                    </td>

                    <td className="px-6 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          size.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        {size.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingSize(size);
                            setShowModal(true);
                          }}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border text-gray-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800"
                          title="Edit size"
                        >
                          <FiEdit2 />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDelete(size)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border text-red-500 hover:border-red-200 hover:bg-red-50"
                          title="Delete size"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <ClientPagination pagination={sizesPagination} label="sizes" />
      </div>

      <SizeModal
        open={showModal}
        onClose={handleCloseModal}
        onSave={handleSave}
        initialData={editingSize}
        sizeGroups={sizeGroups}
      />
    </div>
  );
}
